import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import axios from "axios";
import ScrollReveal from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";
import Lightbox from "@/components/Lightbox";
import HeroPremium from "@/components/HeroPremium";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = ["Tous", "Construction", "Rénovation", "Toiture", "Plomberie", "Électricité", "Terrassement"];

export default function Realisations() {
  const [items, setItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState("Tous");
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [beforeAfterItems, setBeforeAfterItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/before-after`).then(res => {
      setBeforeAfterItems(res.data.filter(i => i.active));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const cat = activeFilter === "Tous" ? "" : activeFilter;
        const res = await axios.get(`${API}/gallery${cat ? `?category=${cat}` : ""}`);
        setItems(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [activeFilter]);

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(-1), []);
  const prevLightbox = useCallback(() => setLightboxIndex(i => Math.max(0, i - 1)), []);
  const nextLightbox = useCallback(() => setLightboxIndex(i => Math.min(items.length - 1, i + 1)), [items.length]);

  return (
    <div data-testid="realisations-page">
      <SEOHead title="Nos réalisations | VGI Construct – Photos de chantiers à Bruxelles & Brabant" description="Découvrez les chantiers livrés par VGI Construct à Bruxelles et dans le Brabant : rénovations, extensions, toitures, façades, avant/après. Devis gratuit – réponse sous 48 h." path="/realisations" />
      {/* HERO PREMIUM */}
      <HeroPremium
        badge="Notre portfolio"
        titleWhite="67 chantiers"
        titleRed="livrés avec soin"
        photo="https://images.unsplash.com/photo-1638885930125-85350348d266?w=700&q=85&fm=webp"
        photoAlt="Belle rénovation intérieure"
        photoBadgeIcon={Camera}
        photoBadgeTitle="100+ Photos"
        photoBadgeSub="de chantiers"
        decoNumber="04"
      />

      {/* ═══ AVANT / APRES ═══ */}
      {beforeAfterItems.length > 0 && (
        <section style={{ backgroundColor: '#fafafa', padding: '48px 0 56px' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
                  <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Transformations</p>
                  <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
                </div>
                <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, color: '#1a1a1a' }}>Avant / Après</h2>
                <p style={{ color: '#666', fontSize: '15px', marginTop: '8px' }}>Glissez le curseur pour voir la transformation</p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {beforeAfterItems.map((item, i) => (
                <ScrollReveal key={item.id} delay={i * 100}>
                  <BeforeAfterSlider before={item.before_url} after={item.after_url} label={item.label} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section style={{ backgroundColor: '#ffffff', padding: '24px 0 16px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-testid="gallery-filters" className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button key={cat} data-testid={`filter-${cat.toLowerCase()}`} onClick={() => { setActiveFilter(cat); setLoading(true); }}
                className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-all ${
                  activeFilter === cat ? "bg-[#8B0000] text-white" : "bg-white text-[#444444] border border-gray-200 hover:border-[#8B0000] hover:text-[#8B0000]"
                }`}>{cat}</button>
            ))}
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: '#ffffff', padding: '16px 0 64px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => <div key={i} className="bg-gray-200 aspect-[4/3] animate-pulse rounded-sm" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="text-[#444444] text-center py-16">Aucune réalisation dans cette catégorie.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => {
                const thumbUrl = item.image_url.includes('cloudinary') ? item.image_url.replace('/upload/', '/upload/w_400,q_auto,f_auto/') : item.image_url;
                return (
                <div key={item.id} data-testid={`gallery-item-${i}`} className="group relative overflow-hidden cursor-pointer bg-white border border-gray-200 hover:border-[#8B0000] transition-all shadow-sm hover:shadow-md rounded-sm"
                  onClick={() => openLightbox(i)}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={thumbUrl} alt={item.title || item.category || 'Réalisation'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                    <span className="text-[#8B0000] text-xs font-bold uppercase tracking-wider bg-white/90 px-2 py-1 w-fit">{item.category}</span>
                    {item.city && <p className="text-gray-300 text-sm mt-2">{item.city}</p>}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <ScrollReveal delay={300}>
            <div className="text-center mt-16">
              <Link to="/demande-devis" data-testid="realisations-cta" className="btn-primary inline-flex items-center gap-2">Devis gratuit – réponse sous 48 h <ArrowRight size={18} /></Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {lightboxIndex >= 0 && <Lightbox images={items} currentIndex={lightboxIndex} onClose={closeLightbox} onPrev={prevLightbox} onNext={nextLightbox} />}
    </div>
  );
}
